import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import { installationDateFormField } from 'types/assetModel/commonFields'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { parseDate } from 'utils/parsingHelpers'
import {
  optionalNumberSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export interface GeckoPlateJSON {
  type: GeckoFeatureModelType.Plate
  name: string
  feature_geometry: {
    x_start: number
    x_end: number
    y_start: number
    y_end: number
  }
  surface_specs: {
    wall_nominal_thickness: number | undefined
    coating_nominal_thickness: number | undefined
  }
  installation_date: string | undefined
}

export const NewGeckoPlateJSON: GeckoPlateJSON = {
  type: GeckoFeatureModelType.Plate,
  name: 'Plate',
  feature_geometry: {
    x_start: 0,
    x_end: 0,
    y_start: 0,
    y_end: 0,
  },
  surface_specs: {
    wall_nominal_thickness: undefined,
    coating_nominal_thickness: undefined,
  },
  installation_date: undefined,
}

export class GeckoPlate extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.Plate
  readonly geometry: GeckoComponentRect
  readonly wallNominalThickness: GeckoMeasurement | undefined
  readonly coatingNominalThickness: GeckoMeasurement | undefined
  readonly installationDate: Date | undefined

  constructor(
    id: string,
    name: string,
    geometry: GeckoComponentRect,
    wallNominal: GeckoMeasurement | undefined,
    coatingNominal: GeckoMeasurement | undefined,
    installationDate: Date | undefined
  ) {
    super(id)
    this.name = name
    this.geometry = geometry
    this.wallNominalThickness = wallNominal
    this.coatingNominalThickness = coatingNominal
    this.installationDate = installationDate
  }

  public get isXWrapped() {
    return this.geometry.x.start.num >= this.geometry.x.end.num
  }

  public get isYWrapped() {
    return this.geometry.y.start.num >= this.geometry.y.end.num
  }

  public static CreateFromJSON(
    componentID: string,
    plateID: string,
    plateJSON: GeckoPlateJSON
  ): GeckoPlate {
    const dimensionMeasurementUnits = GeckoMeasurementUnit.Meters
    const thicknessMeasurementUnits = GeckoMeasurementUnit.Meters

    const featureGeometryData = plateJSON.feature_geometry as Record<
      string,
      number
    >
    const plateGeometry = GeckoComponentRect.CreateFromXYs(
      componentID as string,
      featureGeometryData.x_start as number,
      featureGeometryData.y_start as number,
      featureGeometryData.x_end as number,
      featureGeometryData.y_end as number,
      dimensionMeasurementUnits,
      dimensionMeasurementUnits
    )

    const surfaceSpecs = plateJSON.surface_specs as Record<string, number>

    const wallNominalThickness =
      surfaceSpecs.wall_nominal_thickness !== undefined
        ? GeckoMeasurement.CreateFromValue(
            surfaceSpecs.wall_nominal_thickness as number,
            thicknessMeasurementUnits
          )
        : undefined
    const coatingNominalThickness =
      surfaceSpecs.coating_nominal_thickness !== undefined
        ? GeckoMeasurement.CreateFromValue(
            surfaceSpecs.coating_nominal_thickness as number,
            thicknessMeasurementUnits
          )
        : undefined

    const installationDate =
      plateJSON.installation_date !== undefined
        ? parseDate(plateJSON.installation_date)
        : undefined
    const plateName = plateJSON.name as string

    return new GeckoPlate(
      plateID,
      plateName,
      plateGeometry,
      wallNominalThickness,
      coatingNominalThickness,
      installationDate
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoPlateJSON>
  ): Map<string, GeckoPlate> {
    return Object.keys(recordsJSON).reduce((resultObj, plateID) => {
      resultObj.set(
        plateID,
        GeckoPlate.CreateFromJSON(componentID, plateID, recordsJSON[plateID])
      )
      return resultObj
    }, new Map<string, GeckoPlate>())
  }

  asJSON() {
    return generatePlate(
      this.name,
      this.geometry,
      this.wallNominalThickness,
      this.coatingNominalThickness,
      this.installationDate
    )
  }
}

export function generatePlate(
  name: string,
  geometry: GeckoComponentRect,
  wallNominalThickness?: GeckoMeasurement,
  coatingNominalThickness?: GeckoMeasurement,
  installationDate?: Date
): GeckoPlateJSON {
  const geometryInMeters = geometry.convertTo(
    GeckoMeasurementUnit.Meters,
    GeckoMeasurementUnit.Meters
  )
  const plateJSON: GeckoPlateJSON = {
    type: GeckoFeatureModelType.Plate,
    name: name,
    installation_date: installationDate
      ? installationDate.toISOString().split('T')[0]
      : undefined,
    feature_geometry: {
      x_start: geometryInMeters.x.start.num,
      y_start: geometryInMeters.y.start.num,
      x_end: geometryInMeters.x.end.num,
      y_end: geometryInMeters.y.end.num,
    },
    surface_specs: {
      wall_nominal_thickness:
        wallNominalThickness !== undefined
          ? wallNominalThickness.num
          : undefined,
      coating_nominal_thickness:
        coatingNominalThickness !== undefined
          ? coatingNominalThickness.num
          : undefined,
    },
  }

  return plateJSON
}

export const PlateFormFields = (plateJSON: GeckoPlateJSON) => {
  return [
    {
      dataKey: 'name',
      label: 'Plate Name',
      value: plateJSON.name,
      unit_magnitude: null,
      initialValue: 'Plate',
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    installationDateFormField(plateJSON.installation_date, false),
    {
      dataKey: 'wall_nominal_thickness',
      label: 'Wall Nominal Thickness',
      value: plateJSON.surface_specs.wall_nominal_thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'coating_nominal_thickness',
      label: 'Coating Nominal Thickness',
      value: plateJSON.surface_specs.coating_nominal_thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'x_start',
      label: 'X Start',
      value: plateJSON.feature_geometry.x_start,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'x_end',
      label: 'X End',
      value: plateJSON.feature_geometry.x_end,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y_start',
      label: 'Y Start',
      value: plateJSON.feature_geometry.y_start,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y_end',
      label: 'Y End',
      value: plateJSON.feature_geometry.y_end,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const PlateJSONFromFormFields = (formFields: FormField[]) => {
  const wallNominalThickness = getFormFieldByDataKey(
    formFields,
    'wall_nominal_thickness'
  )
  const coatingNominalThickness = getFormFieldByDataKey(
    formFields,
    'coating_nominal_thickness'
  )
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )
  return generatePlate(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoComponentRect.CreateFromXYs(
      'fake_component_id',
      getFormFieldByDataKey(formFields, 'x_start')?.value as number,
      getFormFieldByDataKey(formFields, 'y_start')?.value as number,
      getFormFieldByDataKey(formFields, 'x_end')?.value as number,
      getFormFieldByDataKey(formFields, 'y_end')?.value as number,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    ),
    wallNominalThickness?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          wallNominalThickness?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    coatingNominalThickness?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          coatingNominalThickness?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    installationDate?.value !== undefined
      ? new Date(installationDate?.value as string)
      : undefined
  )
}

export const PlateFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.Plate,
  title: 'Plates',
  singularName: 'Plate',
  fields: ['name', 'x_start', 'x_end', 'y_start', 'y_end'],
  sortBy: [
    { fieldName: 'y_start', order: 'asc' },
    { fieldName: 'x_start', order: 'asc' },
  ],
}
